import React, { useEffect, useMemo, useState } from "react";
import BarChart from "components/charts/BarChart";
import { barChartOptionsDailyTraffic } from "variables/charts";
import { MdArrowDropUp, MdOutlineCalendarToday, MdBarChart } from "react-icons/md";
import Card from "components/card";
import { fetchOrdersWeekdayCounts } from "utils/api";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const DailyTraffic = () => {
  const [totalOrders, setTotalOrders] = useState(0);
  const [weekdayMap, setWeekdayMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const month = useMemo(() => new Date().toISOString().slice(0, 7), []);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await fetchOrdersWeekdayCounts({
          month,
          include_cancelled: false,
        });

        if (ignore) return;

        setTotalOrders(res.total_orders || 0);

        const map = new Map();
        (res.by_weekday || []).forEach((item) => {
          map.set(item.weekday, item.count);
        });
        setWeekdayMap(map);
      } catch (e) {
        if (!ignore) setError("Failed to load");
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [month]);

  const series = useMemo(() => {
    const data = WEEKDAYS.map((wd) => weekdayMap.get(wd) || 0);
    return [
      {
        name: "Orders",
        data,
      },
    ];
  }, [weekdayMap]);

  const chartOptions = useMemo(() => {
    return {
      ...barChartOptionsDailyTraffic,
      xaxis: {
        ...barChartOptionsDailyTraffic.xaxis,
        categories: WEEKDAYS,
      },
    };
  }, []);

  return (
    <Card extra="flex flex-col h-[420px] w-full rounded-[20px] p-4">

      {/* Header Buttons */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:flex-nowrap">
        <button className="flex items-center justify-center gap-2 rounded-lg bg-lightPrimary p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 dark:bg-navy-700 dark:hover:opacity-90">
          <MdOutlineCalendarToday className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="text-xs sm:text-sm font-medium text-gray-600">This month</span>
        </button>
        <button className="flex items-center justify-center rounded-lg bg-lightPrimary p-1.5 sm:p-2 text-brand-500 hover:bg-gray-100 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20">
          <MdBarChart className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      </div>

      {/* Orders Info + Chart */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:flex-nowrap mt-4">
        
        {/* Total Orders Info */}
        <div className="flex flex-col flex-shrink-0">
          <p className="text-3xl sm:text-3xl md:text-3xl font-bold text-navy-700 dark:text-white">
            {loading ? "…" : totalOrders}
          </p>
          <div className="flex flex-col items-start mt-1">
            <p className="text-xs sm:text-sm text-gray-600">Total Orders</p>
            <div className="flex items-center gap-1">
              <MdArrowDropUp className="h-4 w-4 text-green-500" />
              <span className="text-xs font-bold text-green-500">+2.45%</span>
            </div>
          </div>
        </div>

        {/* Bar Chart */}
        <div className="flex-1 w-full h-[300px] sm:h-[340px] md:h-[380px]">
          <BarChart chartData={series} chartOptions={chartOptions} />
        </div>

      </div>
    </Card>
  );
};

export default DailyTraffic;